import { TypographyXS } from "@/components/typography/XS";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useMemo, useState } from "react";
import CreateAppDetail from "../CreateAppDetail";
import { Libraries, LibraryKey, LibraryMap } from "./types/library.type";
import { PythonManifest } from "./types/manifest.type";

export type AdditionalManifestErrors = {
  portError: string | null;
  libraryError: string | null;
  pythonVersionError: string | null;
  appFileError: string | null;
  requirementsError: string | null;
  coverImageError: string | null;
};

type Props = {
  manifest: PythonManifest;
} & AdditionalManifestErrors;

export function AdditionalManifestFields({
  manifest,
  portError,
  libraryError,
  pythonVersionError,
  appFileError,
  requirementsError,
  coverImageError
}: Props) {
  const [selectedLibraryKey, setSelectedLibraryKey] = useState<
    LibraryKey | undefined
  >(manifest.python.library || undefined);

  const selectedLibrary = useMemo(
    () => selectedLibraryKey && LibraryMap[selectedLibraryKey],
    [selectedLibraryKey]
  );

  return (
    <>
      {!manifest.cover_image && (
        <CreateAppDetail
          id="cover-image"
          name="cover-image"
          label="Cover image"
          placeholder="app_cover.jpg"
          defaultValue="app_cover.jpg"
          required
          error={coverImageError}
        />
      )}
      {!manifest.python.library && (
        <div className="grid grid-cols-[20%_1fr] items-center gap-x-4 gap-y-1">
          <Label
            htmlFor="library"
            className="text-right text-brand-gray-700 dark:text-brand-gray-100"
          >
            Library
          </Label>
          <Select
            name="library"
            value={selectedLibraryKey}
            onValueChange={(l) => setSelectedLibraryKey(l as LibraryKey)}
            required
          >
            <SelectTrigger className="dark:border-brand-gray-700">
              <SelectValue placeholder="Select a app library" />
            </SelectTrigger>
            <SelectContent className="dark:bg-brand-gray-900 dark:border-brand-gray-700">
              <SelectGroup>
                <SelectLabel>Libraries</SelectLabel>
                {Libraries.map((library) => (
                  <SelectItem key={library.key} value={library.key}>
                    {library.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {libraryError && (
            <TypographyXS className="col-start-2 ml-2 text-brand-error dark:text-brand-error font-normal tracking-wide">
              {libraryError}
            </TypographyXS>
          )}
        </div>
      )}
      {(manifest.port === undefined || manifest.port === 0) && (
        <CreateAppDetail
          id="port"
          name="port"
          label="Port"
          placeholder={
            typeof selectedLibrary?.port === "number"
              ? String(selectedLibrary?.port)
              : "8080"
          }
          type="number"
          defaultValue={manifest.port || selectedLibrary?.port}
          required
          error={portError}
        />
      )}
      {!manifest.python.version && (
        <CreateAppDetail
          id="python-version"
          name="python-version"
          label="Python Version"
          placeholder="3.12"
          defaultValue={manifest.python.version || "3.12"}
          required
          error={pythonVersionError}
        />
      )}
      {!manifest.python.app_file && (
        <CreateAppDetail
          id="python-app"
          name="python-app"
          label="Python app"
          placeholder="app.py"
          defaultValue={manifest.python.app_file || "app.py"}
          required
          error={appFileError}
        />
      )}
      {!manifest.python.requirements_file && (
        <CreateAppDetail
          id="python-requirements"
          name="python-requirements"
          label="Python requirements"
          placeholder="requirements.txt"
          defaultValue={manifest.python.requirements_file || "requirements.txt"}
          required
          error={requirementsError}
        />
      )}
    </>
  );
}
